import {useDndContext} from '@dnd-kit/core';
import {Flag} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {PhotoGuideLayoutItem} from '../types/PhotoGuideLayoutItem';
import {RenderAlbumImage} from '../types/RenderAlbumImage';
import {getPhotoGuideLayoutItemDndId} from '../utils/getPhotoGuideLayoutItemDndId';
import {getSaleVehiclePhotoTypeLabel} from '../utils/getSaleVehiclePhotoTypeLabel';
import {AlbumPhotoGrid} from './AlbumPhotoGrid';
import {PhotoGuideEmptySlotTile} from './PhotoGuideEmptySlotTile';

interface PhotoGuideSlotGridProps extends RequiredTestIdProps {
  saleVehicleAlbumId: string;
  layoutContainerId: string;
  layout: PhotoGuideLayoutItem[];
  canEdit: boolean;
  isLoading: boolean;
  renderAlbumImage: RenderAlbumImage;
}

/** The ordered Guide Layout: slots (empty or filled) and Extra Photos, one sortable row. */
export function PhotoGuideSlotGrid(props: PhotoGuideSlotGridProps) {
  const {active, over} = useDndContext();
  const layoutImages = props.layout.map((item) => item.image).filter(isNotNil);
  const dndIds = props.layout.map((item, index) =>
    getPhotoGuideLayoutItemDndId(item, props.saleVehicleAlbumId, index)
  );

  return (
    <AlbumPhotoGrid
      containerId={props.layoutContainerId}
      sortableItemIds={dndIds}
      isDropDisabled={!props.canEdit}
      isLoading={props.isLoading}
    >
      {props.layout.map((item, index) => {
        const dndId = dndIds[index];
        const isDropTarget = props.canEdit && over?.id === dndId && active?.id !== dndId;

        return match(item)
          .with({kind: 'slot', image: Pattern.nullish}, (slot) => (
            <PhotoGuideEmptySlotTile
              key={dndId}
              dndId={dndId}
              photoType={slot.photoType}
              isRequired={slot.required}
              canEdit={props.canEdit}
              isDropTarget={isDropTarget}
              data-testid={suffixTestId(`slot-${slot.photoType}`, props)}
            />
          ))
          .with({kind: 'slot', image: Pattern.nonNullable}, (slot) =>
            props.renderAlbumImage(slot.image, layoutImages.indexOf(slot.image), {
              list: layoutImages,
              dndId,
              testId: `slot-${slot.photoType}`,
              isDisabledDndDrag: !props.canEdit,
              isDisabledDndDrop: !props.canEdit,
              isDropTarget,
              caption: (
                <Flag
                  label={getSaleVehiclePhotoTypeLabel(slot.photoType)}
                  colorScheme="blue"
                  isSubtle
                  size="small"
                  data-testid={suffixTestId(`slot-${slot.photoType}-photoType`, props)}
                />
              ),
            })
          )
          .with({kind: 'extra'}, (extra) =>
            props.renderAlbumImage(extra.image, layoutImages.indexOf(extra.image), {
              list: layoutImages,
              dndId,
              testId: `extra-[${index}]`,
              isDisabledDndDrag: !props.canEdit,
              isDisabledDndDrop: !props.canEdit,
              isDropTarget,
              caption: (
                <Flag
                  label={i18n.t('entity.photoGuide.labels.extraPhoto')}
                  colorScheme="neutral"
                  isSubtle
                  size="small"
                />
              ),
            })
          )
          .exhaustive();
      })}
    </AlbumPhotoGrid>
  );
}
